import React, { useMemo, Ref } from 'react';
import { useMediaQuery, Grid } from '@rbx/ui';
import useAppMainConfig from '@modules/ui/shared/hooks/useAppMainConfig';
import useSideBarContent from '@modules/ui/shared/hooks/useSideBarContent';
import MinimumSpecBanner, {
  TMinimumSpecBannerProps,
} from '@modules/layout/topNavigation/components/MinimumSpecBanner';
import Footer from '../../footer/components/Footer';
import AppLayoutMainContent from './AppLayoutMainContent';
import { mainLayoutID, rightNavMinBreakpoint } from '../constants/layoutConstants';
import { useAppConfig } from './AppConfigProvider';
import useAppLayoutMainStyles from './AppLayoutMain.styles';

export type AppLayoutMainProps = {
  testid: string;
  renderBanner?: (className?: string) => JSX.Element | undefined;
  mainContent?: JSX.Element;
  heading?: JSX.Element;
  mainNavbar?: JSX.Element;
  rightNav?: JSX.Element;
} & TMinimumSpecBannerProps;

const AppLayoutMain = React.forwardRef(function AppLayoutMainComponent(
  {
    mainContent,
    renderBanner,
    testid,
    heading,
    mainNavbar,
    rightNav,
    onBannerToggle,
  }: AppLayoutMainProps,
  ref: Ref<HTMLDivElement>,
) {
  // reset scroll position if route change happened
  const { hideInPageNavigation, showSecondaryNavigation, isLandingPage, menu } = useAppConfig();
  const { hasLeftNav, open: leftNavIsOpen } = useSideBarContent();
  const {
    mainMaxWidth,
    rightNav: { isEmpty: rightNavIsEmpty },
  } = useAppMainConfig();
  const enableRightNav = useMediaQuery((theme) => theme.breakpoints.up(rightNavMinBreakpoint));
  const hasRightNav = useMemo(
    () => !hideInPageNavigation && enableRightNav && !rightNavIsEmpty,
    [hideInPageNavigation, enableRightNav, rightNavIsEmpty],
  );
  const {
    classes: { container, content, main, appLayoutMainContentWrapper },
  } = useAppLayoutMainStyles({
    hasLeftNav,
    leftNavIsOpen,
    showSecondaryNavigation,
    mainMaxWidth,
    hasRightNav,
  });

  // * NOTE(@zwang, 03/18/25): the documentation on the menu/nav data structure is very lacking, but
  // * the reason why this would work is because this is the path to the very first item in the nav menu
  const isCurrentPageInEngineSection = menu?.path === '/platform';
  return (
    <Grid
      className={container}
      item
      XSmall
      data-testid={testid ?? null}
      id={mainLayoutID}
      ref={ref}>
      {isCurrentPageInEngineSection && <MinimumSpecBanner onBannerToggle={onBannerToggle} />}
      {(renderBanner && renderBanner(content)) ?? null}
      <div className={content}>
        <Grid container wrap='nowrap' paddingTop={8} className={main}>
          <Grid
            display='flex'
            direction='column'
            className={appLayoutMainContentWrapper}
            item
            XSmall
            // note: these styles only apply when getXLCenterContentMediaQuery(..)
            // is not applied
            XLarge={hasRightNav ? 9 : 12}
            XXLarge={hasRightNav ? 8 : 12}>
            <AppLayoutMainContent heading={heading} mainNavbar={mainNavbar}>
              {mainContent}
            </AppLayoutMainContent>
            <Footer isLandingPage={isLandingPage} hasRightNav={hasRightNav} />
          </Grid>
          {hasRightNav && rightNav}
        </Grid>
      </div>
    </Grid>
  );
});

export default AppLayoutMain;
